import EmbeddableCheckbox from "@components/C138_EmbeddableForm/EmbeddableCheckbox";
import Link from "@components/Link/Link";
import Loader from "@components/Loader/Loader";
import { ProgrammeCTAType } from "@customTypes/ProgrammeCTAType";
import { useSettings } from "@utilities/context/settings";
import { convertToBulletedList } from "@utilities/convertToBulletedList";
import { fetchApi } from "@utilities/fetchApi";
import clsx from "clsx";
import { format, parseISO } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { ProgrammeContext } from "../../context/programmeComparison";
import { UserContext } from "../../context/user";
import styles from "./C254_ProgrammeCalendar.module.scss";
import { getApplicationLink } from "@utilities/applicationLinkHelper";

type Filter = {
  label: string;
  name: string;
  open: boolean;
  options: {
    label: string;
    value: string;
    checked: boolean;
  }[];
};

type MobOpen = boolean | "done";

export const ProgrammeCalendar = () => {
  const { programmes, addProgramme, removeProgramme } =
    useContext(ProgrammeContext);

  const [filters, setFilters] = useState<Filter[]>([]);
  const [mobOpen, setMobOpen] = useState<MobOpen>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [results, setResults] = useState<any>();
  const { siteSettings } = useSettings();
  const reserveAPlace = siteSettings?.programmeCtaPagesCollection?.items.filter(
    ({ type }) => type === ProgrammeCTAType.ReserveAPlace
  )[0];

  const { user } = useContext(UserContext);

  useEffect(() => {
    const getData = async () => {
      const upcomingProgrammes = await fetchApi("/api/getUpcomingProgrammes", {
        programmeType: "EE",
      });

      const dateFilters: Filter = {
        name: "date",
        label: "Date",
        open: false,
        options: [],
      };

      const locationFilters: Filter = {
        name: "location",
        label: "Location",
        open: false,
        options: [],
      };

      const stageFilters: Filter = {
        name: "Career Stage",
        label: "Career Stage",
        open: false,
        options: [],
      };

      const addFilter = (filter, value) => {
        if (!filter.find((f) => f.value === value)) {
          filter.push({
            value: value,
            label: value,
            checked: false,
          });
        }
      };

      const mappedResults = upcomingProgrammes.reduce((acc, curr) => {
        if (curr.programmeImportData) {
          curr.programmeImportData.forEach((p) => {
            const date = format(parseISO(p.programmeStartDate), "MMMM yyyy");

            addFilter(dateFilters.options, date);
            addFilter(locationFilters.options, p.programmeLocation);

            curr.programmeCareerStagesEeList?.forEach((tag) => {
              addFilter(stageFilters.options, tag);
            });

            const applyLink = getApplicationLink({user: !!user, programmeApplyOnlineId: curr.programmeApplyOnlineParameterValue, isSalesforceApplication: curr.applicationInSalesforce, siteSettings});
           
            const course = {
              title: curr.programmeTitle,
              description: curr.programmeDescription,
              delivery: curr.programmeDeliveryText,
              date: format(parseISO(p.programmeStartDate), "dd MMMM yyyy"),
              ProgrammeStartDate: p.programmeStartDate,
              duration: p.programmeDuration,
              availability: null,
              locationFormatted: p.programmeLocation,
              fees: p.programmeFee,
              tags: curr.programmeAttribute,
              infomation: null,
              "Career Stage": curr.programmeCareerStagesEeList,
              location: p.programmeLocation,
              programmeLink: `/${curr.slug}`,
              applyLink: curr.programmeApplyNowExternalLink || applyLink,
              reserveLink: `/${reserveAPlace?.url?.slug}?programmeTypeCode=${curr?.programmeTypeCode}&programmeTypeGroup=EXECED&referPage=/executive-education/programme-calendar`,
              programmeTypeCode: curr.programmeTypeCode,
            };

            if (acc[date]) {
              acc[date].push(course);
            } else {
              acc[date] = [course];
            }
          });
        }

        return acc;
      }, {});

      Object.keys(mappedResults).forEach((month) => {
        mappedResults[month].sort(
          (a, b) =>
            new Date(a.ProgrammeStartDate).getTime() -
            new Date(b.ProgrammeStartDate).getTime()
        );
      });

      setResults({ programmes: mappedResults });
      dateFilters.options.sort((a, b) => {
        return new Date(a.value).getTime() - new Date(b.value).getTime();
      });
      setFilters([stageFilters, locationFilters, dateFilters]);
    };
    getData();
  }, []);

  const filterToggle = (i) => {
    const stateCopy = [...filters];

    stateCopy[i].open = !stateCopy[i].open;

    stateCopy.forEach((filter, ii) => {
      if (ii !== i) {
        filter.open = false;
      }
    });

    setFilters(stateCopy);
  };

  const toggleDate = (key, e) => {
    e.preventDefault();
    const stateCopy = structuredClone(results);

    stateCopy.programmes[key].open = !stateCopy.programmes[key].open;

    setResults(stateCopy);
  };

  const checkToggle = (filterIn, checkboxIn, e) => {
    e.stopPropagation();
    const stateCopy = [...filters];

    stateCopy[filterIn].options[checkboxIn].checked =
      !stateCopy[filterIn].options[checkboxIn].checked;

    setFilters(stateCopy);
  };

  const toggleShowFilter = () => {
    setMobOpen(!mobOpen);
  };

  useEffect(() => {
    if (mobOpen) {
      setTimeout(() => {
        setMobOpen("done");
      }, 300);
    }
  }, [mobOpen]);

  const clearAll = (e) => {
    e.preventDefault();
    const stateCopy = [...filters];

    stateCopy.forEach((filter) => {
      filter.options.forEach((option) => {
        option.checked = false;
      });
    });

    setFilters(stateCopy);
  };

  const filterBlur = (i, e) => {
    const stateCopy = [...filters];
    let target = e.relatedTarget;

    if (!e.relatedTarget) {
      target = document.activeElement;
    }

    if (
      !target.classList.contains("value") &&
      (!target || target.type !== "checkbox")
    ) {
      stateCopy[i].open = false;
      setFilters(stateCopy);
    }
  };
  const checkBlur = (i) => {
    const stateCopy = [...filters],
      filtersEl = document.querySelectorAll(".filters .inner .filter");

    setTimeout(() => {
      if (
        !document.activeElement ||
        (!document.activeElement.classList.contains("value") &&
          //@ts-expect-error type error
          document.activeElement.type !== "checkbox" &&
          document.activeElement != filtersEl[i])
      ) {
        stateCopy[i].open = false;
        setFilters(stateCopy);
      }
    }, 50);
  };

  const getSelectedFilters = (stateCopy) => {
    return stateCopy.filters
      .map((filter, i) => {
        const returnFilter = Object.assign({}, filter);
        let selected = filter.options.map((option, i) => {
          if (option.checked) {
            option.index = i;
            return option;
          }
          return false;
        });

        selected = selected.filter((a) => {
          return a;
        });

        if (selected.length) {
          returnFilter.options = selected;
          returnFilter.index = i;
          return returnFilter;
        }
        return false;
      })
      .filter((a) => a);
  };

  const getFilteredResults = (filteredResults, selectedFilters) => {
    if (!results) return filteredResults;
    Object.keys(results.programmes).forEach((dateGroupKey) => {
      let dateIndex,
        includeDate = false;

      const includes: unknown[] = [];

      selectedFilters.forEach((filter, i) => {
        if (filter.name === "date") {
          dateIndex = i;
        } else {
          includes.push(false);
        }
      });

      if (dateIndex !== undefined) {
        selectedFilters[dateIndex].options.forEach((option) => {
          if (dateGroupKey === option.value) {
            includeDate = true;
          }
        });
      } else {
        includeDate = true;
      }

      if (includeDate) {
        let options = results.programmes[dateGroupKey].slice(0);

        options = options.map((option) => {
          const optionIncludes = includes.slice(0);
          let x = 0;

          selectedFilters.forEach((filter, i) => {
            if (i !== dateIndex) {
              let include = false;

              filter.options.forEach((filterOption) => {
                if (Array.isArray(option[filter.name])) {
                  if (option[filter.name].indexOf(filterOption.value) > -1) {
                    include = true;
                  }
                } else {
                  if (option[filter.name] === filterOption.value) {
                    include = true;
                  }
                }
              });
              optionIncludes[x] = include;
              x += 1;
            }
          });

          if (optionIncludes.indexOf(false) < 0) {
            return option;
          }

          return false;
        });
        options = options.filter((option) => option);
        if (options.length) {
          filteredResults[dateGroupKey] = options;
          filteredResults[dateGroupKey].open =
            results.programmes[dateGroupKey].open;
        }
      }
    });

    return filteredResults;
  };

  const overLayCheck = (e, programme) => {
    if (e.target.checked) {
      addProgramme({
        programmeTitle: programme.title,
        programmeCode: programme.programmeTypeCode,
        programmeType: "EE",
      });
      return;
    }

    removeProgramme({
      programmeTitle: programme.title,
      programmeCode: programme.programmeTypeCode,
    });
  };

  const computeFilterHeight = () => {
    if (mobOpen && mobOpen === "done") {
      return { height: "auto" };
    } else if (mobOpen) {
      return {
        height:
          document?.querySelector(".filters .inner")?.getBoundingClientRect()
            .height + "px",
      };
    }

    return {};
  };
  let totalCount = 0;
  let filteredResults = {};
  const selectedFilters = getSelectedFilters({ filters: [...filters] });
  filteredResults = getFilteredResults(filteredResults, selectedFilters);

  useEffect(() => {
    if (results && filters) {
      setIsLoading(false);
    }
  }, [results, filters]);

  return (
    <div className={styles["programme-calendar"]}>
      <div className="wrapper">
        <div className={styles.filters} style={computeFilterHeight()}>
          <div className={styles.inner}>
            {filters.map((filter, i) => {
              let count: any = 0;

              filter.options.forEach((option) => {
                if (option.checked) {
                  count += 1;
                  totalCount += 1;
                }
              });
              if (!count) {
                count = false;
              }
              return (
                <button
                  key={`${filter.label}${i}`}
                  tabIndex={0}
                  className={styles.filter}
                  data-open={filter.open}
                  onBlur={(e) => {
                    filterBlur(i, e);
                  }}
                  onClick={() => {
                    filterToggle(i);
                  }}
                >
                  <div className={styles.header} data-count={count || null}>
                    {filter.label}
                  </div>
                  <div className={styles.values}>
                    <div className={styles.inner}>
                      {filter.options.map((value, ii) => (
                        <EmbeddableCheckbox
                          label={value.label}
                          customClass={styles.value}
                          key={`${value.value}${i}`}
                          onBlur={() => {
                            checkBlur(i);
                          }}
                          onChange={(e) => {
                            checkToggle(i, ii, e);
                          }}
                          id={`${filter.label}-${value.value}`}
                          checked={value.checked}
                        />
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <button
          className={clsx("cta", "secondary", styles["show-filters"])}
          data-count={(!mobOpen && totalCount) || null}
          onClick={toggleShowFilter}
        >
          {mobOpen ? "Hide" : "Show"} Filters
        </button>
        <div className={styles["filter-clear"]}>
          {selectedFilters.map(function (filter, i) {
            return (
              <div className={styles.filter} key={`${filter.label}${i}`}>
                <label>{filter.label}:</label>
                <div className={styles.items}>
                  {filter.options.map((option) => (
                    <a
                      key={`${option.label}${i}`}
                      href={"#" + option.label}
                      className={clsx(
                        styles.option,
                        "flex items-baseline gap-2"
                      )}
                      onClick={(e) => {
                        checkToggle(filter.index, option.index, e);
                      }}
                    >
                      {option.label}{" "}
                      <svg
                        width="20"
                        height="20"
                        viewBox="5 5 14 14"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="#fff"
                        focusable="false"
                      >
                        <path
                          d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                          stroke="none"
                          strokeWidth="1"
                          fillRule="evenodd"
                        ></path>
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
          {selectedFilters.length > 0 && (
            <a
              href="#clear-all"
              className={clsx(styles["clear-all"], "flex items-baseline gap-2")}
              onClick={clearAll}
            >
              <svg
                width="20"
                height="20"
                viewBox="5 5 14 14"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                fill="#fff"
                focusable="false"
              >
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  stroke="none"
                  strokeWidth="1"
                  fillRule="evenodd"
                ></path>
              </svg>{" "}
              Clear Filters
            </a>
          )}
        </div>
        <div className={styles["search-results"]}>
          {Object.keys(filteredResults)
            .sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf())
            .map(function (key, i) {
              if (filteredResults[key].length) {
                return (
                  <div className={styles["date-group"]} key={`${key}${i}`}>
                    <a
                      href={"#" + key}
                      className={styles.date}
                      data-open={filteredResults[key].open}
                      onClick={(e) => {
                        toggleDate(key, e);
                      }}
                    >
                      {key}
                      <span>
                        {filteredResults[key].length}{" "}
                        {filteredResults[key].length > 1
                          ? "programmes"
                          : "programme"}
                      </span>
                    </a>
                    <div className={styles.programmes}>
                      {filteredResults[key].map(function (programme, i) {
                        return (
                          <div
                            className={styles["course-search-results"]}
                            data-guid={`item-${key}-${i}`}
                            key={`${key}${i}`}
                          >
                            <div
                              className={`${styles["application-wrapper"]} wrapper`}
                            >
                              <div className="row">
                                <div className="base12">
                                  <div className={styles["application-box"]}>
                                    <div className={styles["left-section"]}>
                                      <h4>
                                        <Link
                                          href={programme.programmeLink || "#"}
                                          className="CoveoResultLink title-link"
                                        >
                                          {programme.title}
                                        </Link>
                                      </h4>
                                      <p>{programme.description}</p>
                                      <div className={`form ${styles["form"]}`}>
                                        <EmbeddableCheckbox
                                          customClass={styles["checkbox"]}
                                          id={"demo-" + key + i}
                                          label="Compare"
                                          onChange={(e) => {
                                            overLayCheck(e, programme);
                                          }}
                                          checked={
                                            !!programmes.find(
                                              (p) =>
                                                p.programmeCode ===
                                                programme.programmeTypeCode
                                            )
                                          }
                                        />
                                      </div>
                                      <div
                                        className={clsx(
                                          styles["tag-info"],
                                          styles["m-hide"]
                                        )}
                                      >
                                        {programme.tags &&
                                          programme.tags.length > 0 && (
                                            <span
                                              className={styles["tag-label"]}
                                            >
                                              Tags
                                            </span>
                                          )}
                                        <span>
                                          {programme.tags &&
                                            programme.tags.join(", ")}
                                        </span>
                                        {programme.infomation && (
                                          <div
                                            className={
                                              styles["message-wrapper"]
                                            }
                                          >
                                            <p className={styles.message}>
                                              {programme.infomation}
                                              <a
                                                href="#close"
                                                aria-label="Close"
                                                className={styles["icon-cross"]}
                                              />
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      <div
                                        className={styles["button-margin"]}
                                      ></div>
                                      <div className={styles["apply-wrapper"]}>
                                        <div
                                          className={styles["button-with-link"]}
                                        >
                                          <Link
                                            href={programme.applyLink || "#"}
                                            className={`${styles.cta} cta`}
                                          >
                                            Apply now
                                          </Link>
                                          {programme.reserveLink != null && (
                                            <Link
                                              href={
                                                programme.reserveLink || "#"
                                              }
                                              className="cta tertiary"
                                            >
                                              <span
                                                className="icon-arrow"
                                                // style="background-image: none;"
                                              >
                                                <svg
                                                  height="20"
                                                  width="20"
                                                  viewBox="4 4 16 16"
                                                  version="1.1"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  focusable="false"
                                                >
                                                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"></path>
                                                </svg>
                                              </span>
                                              <span>Reserve a place</span>
                                            </Link>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className={styles["right-section"]}>
                                      <div className={styles["inner-wrapper"]}>
                                        <div
                                          className={
                                            styles["section-row-wrapper"]
                                          }
                                        >
                                          <p className={styles["section-row"]}>
                                            <span className={styles.label}>
                                              Next
                                            </span>
                                            <span className={styles.info}>
                                              {programme.date}
                                              {programme.availability &&
                                                programme.availability ===
                                                  "Limited Availability" && (
                                                  <span className="sub-text">
                                                    Limited availability
                                                  </span>
                                                )}
                                            </span>
                                          </p>
                                          <p className={styles["section-row"]}>
                                            <span className={styles.label}>
                                              Duration
                                            </span>
                                            <span className={styles.info}>
                                              {programme.duration}
                                            </span>
                                          </p>
                                          <p className={styles["section-row"]}>
                                            <span className={styles.label}>
                                              Location
                                            </span>
                                            <span className={styles.info}>
                                              {programme.locationFormatted}
                                            </span>
                                          </p>
                                          <p className={styles["section-row"]}>
                                            <span className={styles.label}>
                                              Fees
                                            </span>
                                            <span className={styles.info}>
                                              {programme.fees}
                                            </span>
                                          </p>
                                          <span
                                            className={clsx(
                                              styles["section-row"],
                                              styles["rich-text"]
                                            )}
                                          >
                                            <span className={styles.label}>
                                              Delivery
                                            </span>
                                            <span
                                              className={clsx(
                                                styles.info,
                                                styles["delivery-text"]
                                              )}
                                            >
                                              {convertToBulletedList(
                                                programme.delivery
                                              )}
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return false;
            })}
          {isLoading ? (
            <div className={styles.centered}>
              <Loader
                color="#001e62"
                size={70}
                aria-label="Loading Spinner"
                data-testid="loader"
              />
              <p>Loading...</p>
            </div>
          ) : (
            Object.keys(filteredResults).length === 0 && (
              <div className={styles["no-results"]}>No Results available</div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgrammeCalendar;
